import type { DashboardAlertType } from '@features/dashboard/constants/dashboardAlerts';

export interface DashboardMeta {
  scope: 'CHURCH' | 'DISTRICT' | 'NATION';
  scope_ref_id: number;
  scope_name: string;
  generated_at: string;
  primary_open_period_id: number | null;
  primary_period_name?: string | null;
  reporting_progress_pipeline?: string[];
}

export interface DashboardAlert {
  type: DashboardAlertType;
  severity: 'info' | 'warning' | 'danger';
  message?: string;
  count?: number;
  report_tag_id?: number;
  period_name?: string | null;
  deadline?: string | null;
  days_until_deadline?: number | null;
}

export type DeadlineUrgency = 'info' | 'warning' | 'danger';

export interface DashboardPeriod {
  report_tag_id: number;
  name: string;
  since?: string | null;
  until?: string | null;
  deadline?: string | null;
  status: string;
  days_until_deadline?: number | null;
  deadline_urgency?: DeadlineUrgency | null;
}

export interface DashboardPeriods {
  open: DashboardPeriod[];
  deadline_attention?: DashboardPeriod[];
}

export interface DashboardWorkQueueGroup {
  draft?: number;
  needs_attention?: number;
  awaiting_my_approval?: number;
}

export interface DashboardWorkQueue {
  activity?: DashboardWorkQueueGroup;
  department_reports?: DashboardWorkQueueGroup;
  pastor_report?: DashboardWorkQueueGroup;
  finance_statistics?: DashboardWorkQueueGroup;
}

export interface DashboardDepartmentProgress {
  department_id: number;
  department_name: string;
  is_required?: boolean;
  status: string;
  status_label: string;
  department_percent: number;
  period_contribution_percent: number;
  leadership_type?: string | null;
}

export interface DashboardEntityProgress {
  church_id?: number;
  church_name?: string;
  district_id?: number;
  district_name?: string;
  status: string;
  status_label: string;
  reporting_progress_percent: number;
}

export interface DashboardChildBucket {
  approved: number;
  outstanding: number;
  approved_items: Array<{ id: number; name: string }>;
  outstanding_items: Array<{ id: number; name: string }>;
}

export interface DashboardReportingProgressPeriod {
  report_tag_id: number;
  period_name: string;
  since?: string | null;
  until?: string | null;
  deadline?: string | null;
  days_until_deadline?: number | null;
  deadline_urgency?: DeadlineUrgency | null;
  status: 'not_started' | 'in_progress' | 'complete' | string;
  summary: {
    required: number;
    total?: number;
    reporting_progress_percent: number;
  };
  departments?: DashboardDepartmentProgress[];
  entities?: DashboardEntityProgress[];
  office_progress?: {
    reporting_progress_percent: number;
    departments?: DashboardDepartmentProgress[];
  };
  children_progress?: {
    reporting_progress_percent: number;
    entities?: DashboardEntityProgress[];
    churches?: DashboardChildBucket;
    districts?: DashboardChildBucket;
  };
}

export interface DashboardChecklistTemplateItem {
  id: number;
  name: string;
  department_id: number;
  leadership_type?: string | null;
  is_submitted: boolean;
}

export interface DashboardChecklistDepartmentItem {
  department_id: number;
  department_name: string;
  leadership_type?: string | null;
  is_approved: boolean;
  status_label: string;
  children_approved?: number;
  children_outstanding?: number;
  readiness?: 'complete' | 'ready' | 'blocked';
}

export interface DashboardReportingChecklistPeriod {
  report_tag_id: number;
  period_name: string;
  since?: string | null;
  until?: string | null;
  deadline?: string | null;
  days_until_deadline?: number | null;
  deadline_urgency?: DeadlineUrgency | null;
  templates: {
    submitted: number;
    outstanding: number;
    items: DashboardChecklistTemplateItem[];
  };
  department_reports: {
    approved: number;
    outstanding: number;
    items: DashboardChecklistDepartmentItem[];
  };
}

export interface DashboardInvitations {
  pending: number;
  expired_pending: number;
  expiring_within_7_days: number;
}

export interface DashboardRecentActivity {
  activity_id: number;
  uid: number;
  display_name: string;
  department_name?: string | null;
  description: string;
  action: string;
  occurred_at: string;
}

export interface DashboardFinanceSnapshotMetrics {
  total_tithes: number | null;
  total_general_offering: number | null;
  membership_this_month: number | null;
  membership_last_month: number | null;
  membership_trend: 'up' | 'down' | 'flat' | null;
}

export interface DashboardFinanceSnapshotPeriod {
  report_tag_id: number;
  period_name: string;
  since?: string | null;
  until?: string | null;
  status: 'open' | 'closed' | 'pending' | string;
  department_id: number;
  metrics: DashboardFinanceSnapshotMetrics;
}

export interface DashboardResponse {
  meta: DashboardMeta;
  alerts?: DashboardAlert[];
  periods?: DashboardPeriods;
  work_queue?: DashboardWorkQueue;
  reporting_checklist?: DashboardReportingChecklistPeriod[];
  reporting_progress?: DashboardReportingProgressPeriod[];
  invitations?: DashboardInvitations;
  recent_activity?: DashboardRecentActivity[];
  finance_snapshots?: DashboardFinanceSnapshotPeriod[];
}

export type WorkQueueGroupKey = keyof DashboardWorkQueue;

export type WorkQueueMetricKey = keyof DashboardWorkQueueGroup;

export const WORK_QUEUE_METRIC_LABELS: Record<WorkQueueMetricKey, string> = {
  draft: 'Draft',
  needs_attention: 'Needs Attention',
  awaiting_my_approval: 'Awaiting Approval',
};

export const WORK_QUEUE_GROUP_CONFIG: Record<
  WorkQueueGroupKey,
  {
    title: string;
    subtitle: string;
    pathPrefix: string;
    metrics: WorkQueueMetricKey[];
  }
> = {
  activity: {
    title: 'Activity Submission',
    subtitle: 'Across all open reporting periods',
    pathPrefix: '/activity-submissions',
    metrics: ['draft', 'needs_attention'],
  },
  department_reports: {
    title: 'Department Reports',
    subtitle: 'Across all open reporting periods',
    pathPrefix: '/department-reports',
    metrics: ['draft', 'needs_attention', 'awaiting_my_approval'],
  },
  pastor_report: {
    title: 'Pastor Report',
    subtitle: 'Across all open reporting periods',
    pathPrefix: '/leadership-reports/pastor',
    metrics: ['draft', 'needs_attention', 'awaiting_my_approval'],
  },
  finance_statistics: {
    title: 'Finance & Statistics',
    subtitle: 'Across all open reporting periods',
    pathPrefix: '/leadership-reports/finance-statistics',
    metrics: ['draft', 'needs_attention', 'awaiting_my_approval'],
  },
};

export const WORK_QUEUE_METRIC_PATH_SUFFIX: Record<WorkQueueMetricKey, string> = {
  draft: '/draft',
  needs_attention: '/needs-attention',
  awaiting_my_approval: '/awaiting-approval',
};
